import AppLayout from "@layouts/AppLayout";
import RootLayout from "@layouts/RootLayout";
import Feeds from "@pages/Feeds/Feeds";
import Payment from "@pages/Payment/Payment";
import NotFound from "@pages/NotFound/NotFound";
import RefundPolicy from "@pages/RefundPolicy/RefundPolicy";
import ShippingAndDelivery from "@pages/ShippingAndDelivery/ShippingAndDelivery";
import PrivacyPolicy from "@pages/PrivacyPolicy/PrivacyPolicy";
import ContactUs from "@pages/ContactUs/ContactUs";
import TermsAndConditions from "@pages/TermsAndConditions/TermsAndConditions";
import ActivationGuides from "@pages/ActivationGuides/ActivationGuides";
import StoreGuide from "@pages/ActivationGuides/StoreGuide";
import HomeRedesign from "@pages/HomeRedesign";
import ProductDetailsRedesign from "@pages/ProductDetailsRedesign/ProductDetailsRedesign";
import WishlistRedesign from "@pages/WishlistRedesign/WishlistRedesign";
import OrdersRedesign from "@pages/OrdersRedesign/OrdersRedesign";
import { createBrowserRouter, Params } from "react-router";
import { storesAPI } from "@api/main";
import AuthLayout from "@layouts/AuthLayout";
import Banner from "@layouts/Banner/Banner";
import SearchPage from "@pages/SearchPage/SearchPage";
import SearchMobilePage from "@pages/SearchPage/SearchMobilePage";
import CategoryPageRedesign from "@pages/HomeRedesign/CategoryPageRedesign/CategoryPageRedesign";
import CategoriesPageRedesign from "@pages/HomeRedesign/CategoriesPageRedesign/CategoriesPageRedesign";
import CollectionExpandPage from "@pages/HomeRedesign/CollectionExpandPage/CollectionExpandPage";
import CartRedesign from "@pages/CartRedesign/CartRedesign";
import CheckoutRedesign from "@pages/CheckoutRedesign/CheckoutRedesign";
import ServerMaintenancePage from "@pages/ServerMaintenance/ServerMaintenancePage";
import { extractProductIdFromUrl } from "@utils/urlUtils";
import { withLocaleInitialization } from "@utils/apiUtils";
import {
  RedirectCheckoutRedesign,
  RedirectProductSlugToView,
  RedirectToCart,
  RedirectToOrders,
  RedirectToWishlist,
} from "./routeRedirects";

export const detailsLoader = async ({
  params,
}: {
  params: Params<"product_slug">;
}) => {
  // Extract product ID from the SEO-friendly URL format
  const productId = extractProductIdFromUrl(params.product_slug as string);
  
  if (!productId) {
    throw new Error("Invalid product URL format");
  }
  
  const details = await withLocaleInitialization(() => storesAPI.getProduct(productId));
  return details;
};

const router = createBrowserRouter([
  {
    Component: RootLayout,
    children: [
  {
    path: "/",
    Component: HomeRedesign,
    ErrorBoundary: NotFound,
  },
  {
    path: "/search",
    Component: SearchPage,
    ErrorBoundary: NotFound,
  },
  {
    path: "/search-mobile",
    Component: SearchMobilePage,
    ErrorBoundary: NotFound,
  },
  {
    path: "/category-search",
    Component: CategoryPageRedesign,
    ErrorBoundary: NotFound,
  },
  {
    path: "/categories",
    Component: CategoriesPageRedesign,
    ErrorBoundary: NotFound,
  },
  {
    path: "/collection/:feedId",
    Component: CollectionExpandPage,
    ErrorBoundary: NotFound,
  },
  {
    path: "/maintenance",
    Component: ServerMaintenancePage,
  },
  {
    path: "/view/:product_slug",
    Component: ProductDetailsRedesign,
    loader: detailsLoader,
    ErrorBoundary: NotFound,
  },
  {
    path: "/product/:product_slug",
    Component: RedirectProductSlugToView,
  },
  {
    path: "/cart",
    Component: CartRedesign,
    ErrorBoundary: NotFound,
  },
  {
    path: "/wishlist",
    Component: WishlistRedesign,
    ErrorBoundary: NotFound,
  },
  {
    path: "/orders",
    Component: OrdersRedesign,
    loader: () => withLocaleInitialization(() => storesAPI.getOrders()),
    ErrorBoundary: NotFound,
  },
  {
    path: "/checkout",
    Component: CheckoutRedesign,
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const couponCode = url.searchParams.get("coupon_code") || undefined;
      return withLocaleInitialization(() => storesAPI.checkout(couponCode));
    },
    ErrorBoundary: NotFound,
  },
  {
    path: "/cart-redesign",
    Component: RedirectToCart,
  },
  {
    path: "/wishlist-redesign",
    Component: RedirectToWishlist,
  },
  {
    path: "/orders-redesign",
    Component: RedirectToOrders,
  },
  {
    path: "/checkout-redesign",
    Component: RedirectCheckoutRedesign,
  },
  {
    Component: AppLayout,
    children: [
      {
        Component: Banner,
        children: [
          {
            path: "/feeds",
            Component: Feeds,
            loader: () => withLocaleInitialization(() => storesAPI.getHomeFeed()),
            ErrorBoundary: NotFound,
          },
        ],
      },
      {
        path: "/category",
        Component: CategoryPageRedesign,
        ErrorBoundary: NotFound,
      },
      {
        path: "/refund-and-cancellation-policy",
        Component: RefundPolicy,
      },
      {
        path: "/cancellation-and-refund-policy",
        Component: RefundPolicy,
      },
      {
        path: "/cancellation-and-refunds-policy",
        Component: RefundPolicy,
      },
      {
        path: "/refundpolicy",
        Component: RefundPolicy,
      },
      {
        path: "/refundpolicy.html",
        Component: RefundPolicy,
      },
      {
        path: "/shipping-and-delivery",
        Component: ShippingAndDelivery,
      },
      {
        path: "/privacy-policy",
        Component: PrivacyPolicy,
      },
      {
        path: "/contact-us",
        Component: ContactUs,
      },
      {
        path: "/terms-and-conditions",
        Component: TermsAndConditions,
      },
      {
        path: "/activation-guides",
        Component: ActivationGuides,
        ErrorBoundary: NotFound,
      },
      {
        path: "/activation-guides/:storeId",
        Component: StoreGuide,
        ErrorBoundary: NotFound,
      },
    ],
  },
  {
    Component: AuthLayout,
    children: [
      {
        path: "/payment",
        Component: Payment,
        loader: async ({ request }) => {
          const url = new URL(request.url);
          const couponCode = url.searchParams.get("coupon_code") || undefined;
          return withLocaleInitialization(() => storesAPI.checkout(couponCode));
        },
        ErrorBoundary: NotFound,
      },
    ],
  },
  {
    path: "*",
    Component: NotFound,
  },
    ],
  },
]);

export default router;
